/*
 * Neutral WFS feature helpers.
 *
 * This module defines the map-engine feature shape used between rendering
 * adapters and WFS/WFS-T transport code, plus converters to OpenLayers
 * features for WFS XML encoding.
 */
import GeoJSON from "ol/format/GeoJSON.js";

const DEFAULT_GEOMETRY_NAME = "geom";

const geoJsonFormat = new GeoJSON();

/**
 * Creates a neutral WFS feature object from simple parts.
 * @param {Object} params Feature construction parameters.
 * @param {string|null} [params.id] Server feature id, usually workspace.layer.n.
 * @param {Object} [params.properties] Serializable feature attributes.
 * @param {Object|null} [params.geometry] GeoJSON geometry in EPSG:4326 coordinates.
 * @param {string} [params.geometryName] Geometry property name expected by WFS.
 * @returns {{id: string|null, type: string, properties: Object, geometry: Object|null, geometryName: string}} Neutral feature.
 */
export function createWfsFeature({id = null, properties = {}, geometry = null, geometryName = DEFAULT_GEOMETRY_NAME} = {}) {
    return {
        id,
        type: "Feature",
        properties: {...properties},
        geometry,
        geometryName,
    };
}

/**
 * Converts an OpenLayers feature into a neutral WFS feature.
 * @param {import("ol/Feature.js").default} feature OpenLayers feature instance.
 * @param {string} [geometryName] Geometry property name to preserve.
 * @returns {{id: string|null, type: string, properties: Object, geometry: Object|null, geometryName: string}} Neutral feature.
 */
export function olFeatureToWfsFeature(feature, geometryName = DEFAULT_GEOMETRY_NAME) {
    const geoJson = geoJsonFormat.writeFeatureObject(feature, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:4326",
    });
    return createWfsFeature({
        id: feature.getId() || geoJson.id || null,
        properties: geoJson.properties || {},
        geometry: geoJson.geometry || null,
        geometryName,
    });
}

/**
 * Converts a neutral feature into an OpenLayers feature for WFS encoding.
 * @param {{id?: string|null, properties?: Object, geometry?: Object|null, geometryName?: string}} feature Neutral feature.
 * @param {Object} [options] Conversion options.
 * @param {boolean} [options.forceMulti=false] Converts line and polygon geometries to multi geometries.
 * @param {string} [options.geometryName="geom"] Geometry property name expected by WFS.
 * @returns {import("ol/Feature.js").default} OpenLayers feature.
 */
export function wfsFeatureToOlFeature(feature, options = {}) {
    const geometryName = feature.geometryName || options.geometryName || DEFAULT_GEOMETRY_NAME;
    const geoJson = {
        type: "Feature",
        id: feature.id || undefined,
        properties: sanitizeProperties(feature.properties || {}),
        geometry: options.forceMulti ? forceMultiGeometry(feature.geometry) : feature.geometry,
    };
    const featureFormat = new GeoJSON({
        geometryName,
    });
    const olFeature = featureFormat.readFeature(geoJson, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:4326",
    });
    if (!geoJson.geometry) {
        olFeature.unset(geometryName, true);
    }
    if (feature.id) {
        olFeature.setId(feature.id);
    }
    return olFeature;
}

/**
 * Converts neutral features into OpenLayers features for one WFS operation.
 * @param {Array<Object>} features Neutral features.
 * @param {Object} [options] Conversion options forwarded to wfsFeatureToOlFeature.
 * @returns {Array<import("ol/Feature.js").default>} OpenLayers features.
 */
export function wfsFeaturesToOlFeatures(features, options = {}) {
    return features.map((feature) => wfsFeatureToOlFeature(feature, options));
}

/**
 * Removes attributes that GeoServer does not accept in WFS transactions.
 * @param {Object} properties Feature properties from the neutral feature.
 * @returns {Object} Sanitized shallow copy.
 */
function sanitizeProperties(properties) {
    const result = {...properties};
    delete result.boundedBy;
    return result;
}

/**
 * Converts simple line and polygon geometry types to multi geometries.
 * @param {Object|null} geometry GeoJSON geometry.
 * @returns {Object|null} Geometry suitable for WFS-T serialization.
 */
function forceMultiGeometry(geometry) {
    if (!geometry) {
        return geometry;
    }
    if (geometry.type === "LineString") {
        return {
            type: "MultiLineString",
            coordinates: [geometry.coordinates],
        };
    }
    if (geometry.type === "Polygon") {
        return {
            type: "MultiPolygon",
            coordinates: [geometry.coordinates],
        };
    }
    return geometry;
}
